import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch, apiJson, apiSend } from "@/api/client";
import { invoiceKeys, type PdfFile } from "@/api/invoices";
import { paymentKeys, type PaymentInput } from "@/api/payments";
import { segment, withQuery } from "@/api/query";

/*
 * Mirrors api/src/pbo_invoicing/receipts/schemas.py. A receipt acknowledges payments that
 * are already recorded, in its own financial-year series (PBO/R/26-27/0101).
 */

export type ReceiptLineInput = {
  payment_id: string;
  /** Charges the banks took that we decide not to chase, cleared on the receipt. */
  write_off?: string;
};

export type ReceiptInput = {
  lines: ReceiptLineInput[];
  receipt_date?: string | null;
  notes?: string[];
};

/** One invoice of a collection: what it takes of the money, and any shortfall waived. */
export type CollectionLineInput = { invoice_id: string; amount: string; write_off?: string };

/**
 * Money handed over once for several invoices of one client (PBO/R/26-27/0102). `payment` is
 * the whole sum as the payment form records it; the API records a payment on each invoice,
 * shares the foreign amount out, and issues one receipt - all or nothing.
 */
export type CollectionInput = {
  payment: PaymentInput;
  lines: CollectionLineInput[];
  receipt_date?: string | null;
  notes?: string[];
};

export type ReceiptPaymentLine = {
  payment_id: string;
  invoice_id: string;
  invoice_no: string;
  invoice_date: string;
  received_on: string;
  applied: string;
  write_off: string;
};

export type ReceiptDocument = {
  version: number;
  kind: string;
  sha256: string;
  byte_len: number;
  created_at: string;
  created_by_username: string | null;
  reason: string;
};

export type Receipt = {
  id: string;
  receipt_no: string;
  client_id: string;
  client_code: string;
  client_name: string;
  receipt_date: string;
  currency: string;
  amount: string;
  method: string;
  received_currency: string | null;
  received_amount: string | null;
  state: "issued" | "cancelled";
  cancelled_at: string | null;
  cancelled_by_username: string | null;
  cancel_reason: string | null;
  created_by_username: string | null;
  version: number;
  created_at: string;
  updated_at: string;
  payments: ReceiptPaymentLine[];
  documents: ReceiptDocument[];
};

export type ReceiptRow = {
  id: string;
  receipt_no: string;
  receipt_date: string;
  client_id: string;
  client_code: string;
  client_name: string;
  currency: string;
  amount: string;
  method: string;
  received_currency: string | null;
  received_amount: string | null;
  state: "issued" | "cancelled";
  invoice_nos: string[];
  document_version: number | null;
};

export type ReceiptFilters = { clientId?: string; includeCancelled?: boolean };

export const receiptKeys = {
  all: ["receipts"] as const,
  list: (filters: ReceiptFilters) => ["receipts", "list", filters] as const,
  detail: (id: string) => ["receipts", "detail", id] as const,
};

export function listReceipts(filters: ReceiptFilters): Promise<ReceiptRow[]> {
  return apiJson<ReceiptRow[]>(
    withQuery("/receipts", {
      client_id: filters.clientId,
      include_cancelled: filters.includeCancelled ? "true" : undefined,
    }),
  );
}

export function getReceipt(id: string): Promise<Receipt> {
  return apiJson<Receipt>(`/receipts/${segment(id)}`);
}

export function issueReceipt(input: ReceiptInput): Promise<Receipt> {
  return apiSend<Receipt>("/receipts", "POST", input);
}

export function collectReceipt(input: CollectionInput): Promise<Receipt> {
  return apiSend<Receipt>("/receipts/collect", "POST", input);
}

async function previewPdf(path: string, body: unknown): Promise<PdfFile> {
  const res = await apiFetch(path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return { blob: await res.blob(), filename: "receipt-preview.pdf", sha256: null };
}

/** The receipt POST /receipts would issue, stamped PREVIEW. Takes no number, writes nothing. */
export function previewReceipt(input: ReceiptInput): Promise<PdfFile> {
  return previewPdf("/receipts/preview", input);
}

/** The receipt POST /receipts/collect would issue, stamped PREVIEW; records no payment. */
export function previewCollection(input: CollectionInput): Promise<PdfFile> {
  return previewPdf("/receipts/collect/preview", input);
}

export function cancelReceipt(id: string, reason: string, version: number): Promise<Receipt> {
  return apiSend<Receipt>(`/receipts/${segment(id)}/cancel`, "POST", { reason }, version);
}

export function regenerateReceipt(id: string, reason: string, version: number): Promise<Receipt> {
  return apiSend<Receipt>(
    `/receipts/${segment(id)}/documents/regenerate`,
    "POST",
    { reason },
    version,
  );
}

export function receiptFileName(receipt: { receipt_no: string }): string {
  return `${receipt.receipt_no.replace(/\//g, "-")}.pdf`;
}

export async function fetchReceiptPdf(
  id: string,
  version: number,
  fallbackName = `receipt-v${version}.pdf`,
): Promise<PdfFile> {
  const res = await apiFetch(`/receipts/${segment(id)}/documents/${version}.pdf`);
  const match = /filename="([^"]+)"/.exec(res.headers.get("Content-Disposition") ?? "");
  return {
    blob: await res.blob(),
    filename: match?.[1] ?? fallbackName,
    sha256: res.headers.get("X-Content-SHA256"),
  };
}

/** The newest PDF of a receipt, when all you have is the receipt's id. */
export async function fetchLatestReceiptPdf(receiptId: string): Promise<PdfFile> {
  const receipt = await getReceipt(receiptId);
  const version = receipt.documents[0]?.version ?? 1;
  return fetchReceiptPdf(receiptId, version, receiptFileName(receipt));
}

// --------------------------------------------------------------------------- hooks

export function useReceipts(filters: ReceiptFilters = {}) {
  return useQuery({ queryKey: receiptKeys.list(filters), queryFn: () => listReceipts(filters) });
}

export function useReceipt(id: string) {
  return useQuery({ queryKey: receiptKeys.detail(id), queryFn: () => getReceipt(id) });
}

/** Receipts change invoices and payments too, so everything money-related is refreshed. */
function useReceiptMutation<TVars>(run: (vars: TVars) => Promise<Receipt>) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: run,
    onSuccess: (receipt) => {
      void client.invalidateQueries({ queryKey: receiptKeys.all });
      void client.invalidateQueries({ queryKey: invoiceKeys.all });
      void client.invalidateQueries({ queryKey: paymentKeys.all });
      client.setQueryData(receiptKeys.detail(receipt.id), receipt);
    },
  });
}

export function useIssueReceipt() {
  return useReceiptMutation((input: ReceiptInput) => issueReceipt(input));
}

export function useCollectReceipt() {
  return useReceiptMutation((input: CollectionInput) => collectReceipt(input));
}

export function useCancelReceipt(id: string) {
  return useReceiptMutation(({ reason, version }: { reason: string; version: number }) =>
    cancelReceipt(id, reason, version),
  );
}

export function useRegenerateReceipt(id: string) {
  return useReceiptMutation(({ reason, version }: { reason: string; version: number }) =>
    regenerateReceipt(id, reason, version),
  );
}
